import { Button, Input, SelectV2, TextArea, TextInput } from '@cimplico/components';
import { useNavigate, useParams } from 'react-router-dom';
import { toastNotification } from '../../components/toast/Toast';
import { useFirmContext } from '../../providers/FirmProvider';
import { useForm, Controller } from 'react-hook-form';
import { ChecklistItem } from '../../types/checklist';
import { useUpdateChecklistItems } from '../../queries/use-update-checklist-items';
import ChecklistCategorySelect from '../../components/checklist-category-select/ChecklistCategorySelect';
import { useIsActiveSubscription } from '../../hooks/use-is-active-subscription';

type EditChecklistItemFormProps = {
    checklistItem: ChecklistItem;
};

type ChecklistItemFormFields = {
    name: string;
    description?: string;
    reportCodes: string[];
    categoryId?: string;
};

const NAME_MAX_CHAR = 250;
const DESCRIPTION_MAX_CHAR = 2000;

function EditChecklistItemForm({ checklistItem }: EditChecklistItemFormProps): JSX.Element {
    const navigate = useNavigate();
    const { shortId } = useFirmContext();
    const { id: checklistId } = useParams();

    const activeSubscription = useIsActiveSubscription();

    function handleCancel(): void {
        navigate(`..`);
    }

    const updateChecklistItemQuery = useUpdateChecklistItems(checklistId as string);

    const {
        control,
        formState: { errors, isDirty, dirtyFields },
        setError,
        handleSubmit,
        getValues,
    } = useForm<ChecklistItemFormFields>({
        defaultValues: {
            name: checklistItem.name,
            description: checklistItem.description,
            reportCodes: checklistItem.reportCodes,
            categoryId: checklistItem.category?.id,
        },
    });

    function onSubmit(): void {
        const values = getValues();

        if (values.name.length > NAME_MAX_CHAR) {
            setError('name', { message: `A checklist item name must be within ${NAME_MAX_CHAR} characters.` });
            return;
        }

        if (values.description && values.description.length > DESCRIPTION_MAX_CHAR) {
            setError('description', { message: `A checklist item description must be within ${DESCRIPTION_MAX_CHAR} characters.` });
            return;
        }

        if (values.reportCodes.some((rc) => rc.includes(','))) {
            setError('reportCodes', { message: `Report codes cannot contain commas.` });
            return;
        }

        if (!isDirty) {
            handleCancel();
            return;
        }

        updateChecklistItemQuery.mutate(
            [
                {
                    id: checklistItem.id,
                    fields: {
                        name: dirtyFields.name ? values.name : undefined,
                        description: dirtyFields.description ? values.description : undefined,
                        reportCodes: dirtyFields.reportCodes ? values.reportCodes : undefined,
                        categoryId: dirtyFields.categoryId ? values.categoryId || null : undefined,
                    },
                },
            ],
            {
                onSuccess: () => {
                    navigate(`/${shortId}/templates/checklists/${checklistId}`);
                    toastNotification('success', 'Checklist item details updated!');
                },
                onError: (e) => {
                    const result = e.response?.data.results[0];

                    if (result.error?.fields?.name) {
                        setError('name', { type: 'custom', message: result.error.fields.name });
                    }

                    if (result.error?.fields?.description) {
                        setError('description', { type: 'custom', message: result.error.fields.description });
                    }

                    toastNotification('error', 'Unable to update checklist item!');
                },
            }
        );
    }

    return (
        <form className="flex flex-col gap-8" onSubmit={handleSubmit(onSubmit)}>
            {/* CHECKLIST ITEM NAME */}
            <Controller
                name="name"
                rules={{ required: 'An item name must be provided.' }}
                control={control}
                render={({ field }): JSX.Element => (
                    <TextInput
                        placeholder="e.g. Attach copy of bank statement"
                        title="Name"
                        isError={Boolean(errors.name)}
                        errorMessage={errors?.name?.message?.toString()}
                        onChange={(value): void => field.onChange(value)}
                        characterLimit={NAME_MAX_CHAR}
                        defaultValue={field.value}
                        isDisabled={!activeSubscription}
                        autoFocus
                    />
                )}
            />

            {/* CHECKLIST ITEM REPORT CODE */}
            <Controller
                name="reportCodes"
                rules={{ required: 'A report code must be provided.' }}
                control={control}
                render={({ field, fieldState }): JSX.Element => {
                    return (
                        <SelectV2
                            options={[]}
                            value={field.value.map((f) => {
                                return {
                                    label: f,
                                    value: f,
                                };
                            })}
                            isMulti
                            hideSelectedOptions={false}
                            isCreatable
                            emptyMessage="Start typing to add a report code..."
                            onChange={(value): void => field.onChange(value.map((v) => v.value))}
                            key={field.value.toString()}
                            newOptionActionLabel="Add"
                            title="Report codes"
                            errorMessage={fieldState.error?.message}
                            description="A worksheet will be assigned this item if it's tag matches any value."
                        />
                    );
                }}
            />
            {/* CHECKLIST ITEM DESCRIPTION */}
            <Controller
                name="description"
                control={control}
                render={({ field }): JSX.Element => (
                    <TextArea
                        title="Add a description"
                        placeholder="Description..."
                        isOptional
                        onChange={(value): void => field.onChange(value)}
                        errorMessage={errors?.description?.message?.toString()}
                        defaultValue={field.value}
                        isDisabled={!activeSubscription}
                        characterLimit={DESCRIPTION_MAX_CHAR}
                    />
                )}
            />
            {/* CATEGORY SELECT */}
            {checklistId && (
                <Controller
                    name="categoryId"
                    control={control}
                    render={({ field: { ref, onChange, value } }): JSX.Element => (
                        <ChecklistCategorySelect
                            innerRef={ref}
                            isOptional
                            defaultCategory={value}
                            title="Category"
                            onChange={(v): void => onChange(v[0])}
                            checklistId={checklistId}
                            isDisabled={!activeSubscription || updateChecklistItemQuery.isLoading}
                        />
                    )}
                />
            )}

            <div className="flex justify-end gap-4">
                <Button type="hollow" label="Cancel" onClick={handleCancel} />
                <Button
                    type="primary"
                    label={updateChecklistItemQuery.isLoading ? 'Saving...' : 'Save'}
                    disabled={!activeSubscription || updateChecklistItemQuery.isLoading}
                    isLoading={updateChecklistItemQuery.isLoading}
                    isSubmit
                />
            </div>
        </form>
    );
}

export default EditChecklistItemForm;
